import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBookmarks,
  getBookmarkIds,
  addBookmark,
  removeBookmark,
} from '../services/bookmarks.api';

interface BookmarkContextType {
  bookmarkIds: Set<number>;
  isLoaded: boolean;
  isBookmarked: (articleId: number) => boolean;
  toggleBookmark: (articleId: number) => void;
}

const BookmarkContext = createContext<BookmarkContextType>({
  bookmarkIds: new Set(),
  isLoaded: false,
  isBookmarked: () => false,
  toggleBookmark: () => {},
});

/**
 * Provider that loads bookmark IDs once and shares toggle logic.
 * Place inside AppShell so all authenticated pages can access it.
 */
export const BookmarkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  const { data: ids = [], isSuccess } = useQuery({
    queryKey: ['bookmarks', 'ids'],
    queryFn: getBookmarkIds,
    staleTime: 5 * 60 * 1000,
  });

  const bookmarkIds = useMemo(() => new Set(ids), [ids]);

  const addMut = useMutation({
    mutationFn: addBookmark,
    onMutate: async (articleId) => {
      await queryClient.cancelQueries({ queryKey: ['bookmarks', 'ids'] });
      const prev = queryClient.getQueryData<number[]>(['bookmarks', 'ids']);
      queryClient.setQueryData<number[]>(['bookmarks', 'ids'], (old = []) => [...old, articleId]);
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['bookmarks', 'ids'], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });

  const removeMut = useMutation({
    mutationFn: removeBookmark,
    onMutate: async (articleId) => {
      await queryClient.cancelQueries({ queryKey: ['bookmarks', 'ids'] });
      const prev = queryClient.getQueryData<number[]>(['bookmarks', 'ids']);
      queryClient.setQueryData<number[]>(['bookmarks', 'ids'], (old = []) =>
        old.filter((id) => id !== articleId)
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['bookmarks', 'ids'], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });

  const isBookmarked = useCallback(
    (articleId: number) => bookmarkIds.has(articleId),
    [bookmarkIds]
  );

  const toggleBookmark = useCallback(
    (articleId: number) => {
      if (bookmarkIds.has(articleId)) {
        removeMut.mutate(articleId);
      } else {
        addMut.mutate(articleId);
      }
    },
    [bookmarkIds, addMut, removeMut]
  );

  const value = useMemo(
    () => ({ bookmarkIds, isLoaded: isSuccess, isBookmarked, toggleBookmark }),
    [bookmarkIds, isSuccess, isBookmarked, toggleBookmark]
  );

  return <BookmarkContext.Provider value={value}>{children}</BookmarkContext.Provider>;
};

/**
 * Access the bookmark context (IDs, toggle function).
 */
export const useBookmarkContext = () => useContext(BookmarkContext);

/**
 * Fetch paginated bookmarked articles for the Bookmarks page.
 */
export function useBookmarks(page = 1) {
  return useQuery({
    queryKey: ['bookmarks', 'list', page],
    queryFn: () => getBookmarks(page),
    staleTime: 2 * 60 * 1000,
  });
}
